// Normalizes every failure into one shape and maps it to a *user-safe*
// message. Raw backend error text is NEVER shown to a user (guide rule) —
// it always passes through toUserMessage().

/** Field-path → human message, e.g. { "scheduledAt": "Pick a future time." } */
export type FieldErrors = Record<string, string>;

export type ApiErrorKind =
  | 'validation' // 400 with field errors → render INLINE, never toast
  | 'unauthorized' // 401
  | 'forbidden' // 403
  | 'not-found' // 404
  | 'conflict' // 409
  | 'server' // 5xx
  | 'network' // fetch rejected / offline
  | 'timeout' // aborted by our own timeout
  | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  readonly fieldErrors: FieldErrors;

  constructor(kind: ApiErrorKind, status: number, message: string, fieldErrors: FieldErrors = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 400) return 'validation';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * Pull field errors from the common backend envelopes:
 *   { errors: { field: "msg" | ["msg", …] } }
 *   { fieldErrors: { field: "msg" } }
 *   { detail: [{ loc: [..,"field"], msg }] }   (FastAPI-style)
 */
function extractFieldErrors(body: unknown): FieldErrors {
  const out: FieldErrors = {};
  if (!body || typeof body !== 'object') return out;
  const b = body as Record<string, unknown>;

  const bag = (b.errors ?? b.fieldErrors) as Record<string, unknown> | undefined;
  if (bag && typeof bag === 'object') {
    for (const [key, val] of Object.entries(bag)) {
      out[key] = Array.isArray(val) ? String(val[0]) : String(val);
    }
  }

  if (Array.isArray(b.detail)) {
    for (const item of b.detail as Array<Record<string, unknown>>) {
      const loc = item.loc;
      if (Array.isArray(loc) && loc.length && typeof item.msg === 'string') {
        out[String(loc[loc.length - 1])] = item.msg;
      }
    }
  }
  return out;
}

/** Turn a Response into a typed ApiError (call only for non-ok responses). */
export async function apiErrorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON body — ignore, fall back to status
  }
  const message =
    (body && typeof body === 'object' && 'message' in body
      ? String((body as Record<string, unknown>).message)
      : '') || res.statusText;
  return new ApiError(kindForStatus(res.status), res.status, message, extractFieldErrors(body));
}

/** Normalize anything thrown (including AbortError / TypeError) into an ApiError. */
export function parseApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof DOMException && err.name === 'AbortError') {
    return new ApiError('timeout', 0, 'The request took too long.');
  }
  if (err instanceof TypeError) {
    // fetch throws TypeError on network failure / offline
    return new ApiError('network', 0, 'Network request failed.');
  }
  return new ApiError('unknown', 0, err instanceof Error ? err.message : 'Unknown error.');
}

/** A user-fixable error (bad input / auth) vs. a system fault. */
export function isUserError(err: ApiError): boolean {
  return err.status >= 400 && err.status < 500;
}

const USER_MESSAGES: Record<ApiErrorKind, string> = {
  validation: 'Please check the highlighted fields and try again.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to do that.",
  'not-found': "We couldn't find what you were looking for.",
  conflict: 'That change conflicts with the current state. Refresh and try again.',
  server: 'Something went wrong on our end. Please try again in a moment.',
  network: 'You appear to be offline. Check your connection and try again.',
  timeout: 'The request took too long. Please try again.',
  unknown: 'Something went wrong. Please try again.',
};

/** The ONLY text that should ever reach a toast or banner. */
export function toUserMessage(err: unknown): string {
  const e = parseApiError(err);
  return USER_MESSAGES[e.kind] ?? USER_MESSAGES.unknown;
}
